// Typed API client for miniapp-api. JWT held in memory only (re-fetched on app start).

import {
  setCached, invalidateCached,
  enqueueCheckIn, listQueue, removeFromQueue, bumpAttempts,
  isOnline,
  type QueuedCheckIn,
} from './cache';

const API_BASE = import.meta.env.VITE_API_BASE || 'http://localhost:8080';

// Cache TTLs (ms). Tunable per-route. Tighter = fresher UI but more network;
// looser = faster cold open but staler data.
const TTL_5MIN = 5 * 60 * 1000;
const TTL_30SEC = 30 * 1000;
const TTL_1HOUR = 60 * 60 * 1000;

// Cache keys — version-prefixed in cache.ts via CACHE_VERSION. Bump there to
// invalidate everything on shape changes.
export const CACHE_KEYS = {
  events: 'events:mine',
  balance: 'balance',
  blocks: 'availability:blocks',
  offers: 'offers',
  profile: 'me:profile',
  store: 'store:products',
} as const;

let _token: string | null = null;
let _user: AuthUser | null = null;

export interface UserFlags {
  puedeSerSupervisor: boolean;
  esLider: boolean;
  puedeSerCapitan: boolean;
}

export interface AuthUser {
  personalId: string;
  firstName: string;
  nombre?: string;
  flags: UserFlags;
  /** True when this user appears in Config_Admins with Active=Yes.
   *  Drives Ops tab visibility, chat spy mode, and force-close UI. */
  isAdmin?: boolean;
}

export interface MyEvent {
  id: string;
  title: string;
  date: string;
  startTime: string;
  /** Call-time instant (epoch ms, AST) from the API — authoritative for "already started"; null if the date is unresolvable. */
  startMs?: number | null;
  /** Server verdict for «No puedo ir» (authoritative event date + closure state) — the API enforces the same rule. */
  canCancel?: boolean;
  /** Server verdict for «Cambiar turno» (event not in a protected state AND before the call time). */
  canSwap?: boolean;
  /** Authoritative event date (YYYY-MM-DD) from Asignaciones_Eventos; `date` is the row's copy and can be stale. */
  eventDate?: string;
  endTime: string;
  location: string;
  mapsUrl?: string;
  role: string;
  isCapitan: boolean;
  status: string;
  checkedIn: boolean;
  /** Whole days since the event date — set only for "Pendiente de cierre" items. */
  daysOverdue?: number;
}

export interface MyEventsResponse {
  today: MyEvent[];
  upcoming: MyEvent[];
  completed: MyEvent[];
  /** Past events still awaiting closure (≤7 days old). Drives "Pendiente de cierre". */
  pendingClosure: MyEvent[];
}

export function setToken(t: string | null) { _token = t; }
export function getToken() { return _token; }
export function getUser() { return _user; }

class ApiError extends Error {
  status: number;
  // Full response body so callers can read auxiliary fields (e.g. /my-report
  // 409 returns { error, existing } for the conflict UI).
  body: Record<string, unknown> | null;
  constructor(message: string, status: number, body: Record<string, unknown> | null = null) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

async function request<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((opts.headers as Record<string, string>) || {}),
  };
  if (_token) headers.Authorization = `Bearer ${_token}`;

  const res = await fetch(`${API_BASE}${path}`, { ...opts, headers });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    let body: Record<string, unknown> | null = null;
    try {
      body = await res.json();
      if (body && typeof body === 'object' && typeof body.error === 'string') msg = body.error;
    } catch { /* ignore */ }
    throw new ApiError(msg, res.status, body);
  }
  return res.json() as Promise<T>;
}

// Multipart sibling of request(): do NOT set Content-Type — the browser adds the
// multipart boundary. Keeps the Authorization (JWT) header and the same error shape.
async function requestForm<T>(path: string, form: FormData, opts: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = { ...((opts.headers as Record<string, string>) || {}) };
  if (_token) headers.Authorization = `Bearer ${_token}`;
  const res = await fetch(`${API_BASE}${path}`, { method: 'POST', ...opts, body: form, headers });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    let body: Record<string, unknown> | null = null;
    try {
      body = await res.json();
      if (body && typeof body === 'object' && typeof body.error === 'string') msg = body.error;
    } catch { /* ignore */ }
    throw new ApiError(msg, res.status, body);
  }
  return res.json() as Promise<T>;
}

// ---- Client-deposit (factura proof) ---------------------------------------
export interface DepositoStatus {
  estado: string; // Pendiente | En_Revision | Conciliado | Mensual | No_Aplica
  metodo?: string;
  match?: string;
  detectedAmount?: number | null;
  confianza?: string;
  tieneVoucher?: boolean;
  expected?: number;
}
export interface DepositoUploadResult {
  ok: boolean;
  estado: string;
  match: string;
  detectedAmount: number | null;
  confidence: string;
  expected: number;
}

export async function getDepositoStatus(eventId: string): Promise<DepositoStatus> {
  return request<DepositoStatus>(`/events/${encodeURIComponent(eventId)}/deposito-status`);
}

export async function uploadDepositoVoucher(eventId: string, file: File, metodo: string): Promise<DepositoUploadResult> {
  const form = new FormData();
  form.append('voucher', file);
  if (metodo) form.append('metodo', metodo);
  return requestForm<DepositoUploadResult>(`/events/${encodeURIComponent(eventId)}/deposito-voucher`, form);
}

export async function authVerify(initData: string): Promise<{ token: string; user: AuthUser }> {
  const r = await request<{ token: string; user: AuthUser }>(
    '/auth/verify',
    { method: 'POST', body: JSON.stringify({ initData }) }
  );
  setToken(r.token);
  _user = r.user;
  return r;
}

export async function getMyEvents(): Promise<MyEventsResponse> {
  const r = await request<MyEventsResponse>('/events/mine');
  setCached(CACHE_KEYS.events, r, TTL_5MIN);
  return r;
}

/**
 * Check in to an event. Offline-aware:
 *   - Captures the CLIENT timestamp at the moment of tap, not when the request
 *     finishes. Staff at venues with poor connectivity get credited for when
 *     they actually arrived, not when the network catches up hours later.
 *   - If online: POSTs immediately with `clientTimestamp` so the server
 *     persists the real arrival time (server bounds-checks ±6h).
 *   - If offline OR network fails: queues the write in localStorage, returns
 *     optimistic success with `queued: true`. The queue drains automatically
 *     on next 'online' event or next app open with connectivity.
 *   - Idempotent at the queue level: tapping twice doesn't stack duplicates.
 */
export async function checkIn(
  eventId: string,
  geo?: { lat?: number; lng?: number }
): Promise<{ ok: boolean; checkedInAt: string; queued?: boolean; offline?: boolean }> {
  const clientTimestamp = new Date().toISOString();

  if (!isOnline()) {
    enqueueCheckIn({ eventId, geo });
    invalidateCached(CACHE_KEYS.events); // forces stale flag on next render
    return { ok: true, checkedInAt: clientTimestamp, queued: true, offline: true };
  }

  try {
    const r = await request<{ ok: boolean; checkedInAt: string }>(
      `/events/${encodeURIComponent(eventId)}/checkin`,
      { method: 'POST', body: JSON.stringify({ ...(geo || {}), clientTimestamp }) }
    );
    invalidateCached(CACHE_KEYS.events);
    return { ...r, queued: false };
  } catch {
    // Network errored even though navigator.onLine said true — DNS hiccup,
    // CORS, captive portal, etc. Treat same as offline: queue + optimistic ok.
    enqueueCheckIn({ eventId, geo });
    invalidateCached(CACHE_KEYS.events);
    return { ok: true, checkedInAt: clientTimestamp, queued: true, offline: true };
  }
}

/**
 * Drain the offline check-in queue. Called on:
 *   - App boot (after authVerify succeeds)
 *   - 'online' event (browser reconnects)
 * Each queued item is replayed with its saved clientTimestamp. Idempotency on
 * the server side (Upstash key keyed by personalId+eventId) makes this safe
 * to call repeatedly. On success we remove from queue and invalidate the
 * events cache. On failure we bump the attempt counter and leave it queued.
 */
export async function drainCheckInQueue(): Promise<{ drained: number; failed: number }> {
  if (!isOnline() || !_token) return { drained: 0, failed: 0 };
  const queue = listQueue();
  const checkins = queue.filter((q): q is QueuedCheckIn => q.kind === 'checkin');
  if (checkins.length === 0) return { drained: 0, failed: 0 };

  let drained = 0;
  let failed = 0;
  for (const item of checkins) {
    try {
      await request<{ ok: boolean; checkedInAt: string }>(
        `/events/${encodeURIComponent(item.eventId)}/checkin`,
        {
          method: 'POST',
          body: JSON.stringify({
            ...(item.geo || {}),
            clientTimestamp: item.clientTimestamp,
          }),
        }
      );
      removeFromQueue(q => q.kind === 'checkin' && q.eventId === item.eventId);
      drained++;
    } catch (e) {
      bumpAttempts(q => q.kind === 'checkin' && q.eventId === item.eventId);
      failed++;
    }
  }
  if (drained > 0) invalidateCached(CACHE_KEYS.events);
  return { drained, failed };
}

export interface CancelEventResponse {
  ok: boolean;
  isLate: boolean;
  penalty: { id: string; monto: number } | null;
}

export async function cancelEvent(eventId: string, reason?: string): Promise<CancelEventResponse> {
  const r = await request<CancelEventResponse>(
    `/events/${encodeURIComponent(eventId)}/cancel`,
    { method: 'POST', body: JSON.stringify({ reason: reason || '' }) }
  );
  // Cancellation invalidates events list (estado flip) + balance (penalty
  // possibly applied) + offers (a related offer may be obsolete).
  invalidateCached(CACHE_KEYS.events, CACHE_KEYS.balance, CACHE_KEYS.offers);
  return r;
}

export interface LedgerEvent {
  eventoId: string;
  fecha: string;
  cliente: string;
  rol: string;
  salario: number;
  propinas: number;
  total: number;
  estado: string;
  // July-2026 cobro gate: true = the pay for this event is retained until the
  // client's payment is validated (it is NOT in `saldo` yet). Optional so a
  // stale backend without the field renders as before.
  pendienteCobro?: boolean;
}

export interface ActivePenalty {
  id: string;
  tipo: string;            // 'Strike' | 'Multa' | ...
  monto: number;
  saldoPendiente: number;
  motivo: string;
  fecha: string;
  estado: string;
  eventoId: string;
}

export interface PayoutRow {
  id: string;
  monto: number;
  fecha: string;
  estado: string;
  eventos?: string;
}

export interface BalanceResponse {
  saldo: number;
  penalidad: number;
  neto: number;
  ultimoPago: string | null;
  pendingPayout: { monto: number; fecha: string; eventos: string } | null;
  recentEvents: LedgerEvent[];
  activePenalties: ActivePenalty[];
  recentPayouts: PayoutRow[];
}

export async function getBalance(): Promise<BalanceResponse> {
  const r = await request<BalanceResponse>('/balance');
  setCached(CACHE_KEYS.balance, r, TTL_5MIN);
  return r;
}

export interface PayoutRequestResponse {
  ok: boolean;
  cached?: boolean;
  pagoId?: string;
  monto?: number;
  estado?: string;
}

export async function requestPayout(): Promise<PayoutRequestResponse> {
  const r = await request<PayoutRequestResponse>('/payout/request', { method: 'POST', body: '{}' });
  invalidateCached(CACHE_KEYS.balance); // pendingPayout shape changed
  return r;
}

// ============ Disponibilidad ============

export type BlockTipo = 'Vacaciones' | 'Enfermedad' | 'Bloqueo_Personal';

export interface AvailabilityBlock {
  from: string;
  to: string;
  tipo: BlockTipo;
  notas: string;
  rowNumbers: number[];
}

export async function getAvailabilityBlocks(): Promise<{ blocks: AvailabilityBlock[] }> {
  const r = await request<{ blocks: AvailabilityBlock[] }>('/availability/blocks');
  setCached(CACHE_KEYS.blocks, r, TTL_5MIN);
  return r;
}

export async function addAvailabilityBlock(input: {
  from: string;
  to: string;
  tipo: BlockTipo;
  notas?: string;
}): Promise<{ ok: boolean; count: number }> {
  const r = await request<{ ok: boolean; count: number }>('/availability/block', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  invalidateCached(CACHE_KEYS.blocks);
  return r;
}

export async function removeAvailabilityBlock(input: {
  from: string;
  to: string;
  tipo?: BlockTipo;
}): Promise<{ ok: boolean; count: number }> {
  const r = await request<{ ok: boolean; count: number }>('/availability/block', {
    method: 'DELETE',
    body: JSON.stringify(input),
  });
  invalidateCached(CACHE_KEYS.blocks);
  return r;
}

// ============ Perfil ============

export interface ProfileInfo {
  personalId: string;
  nombre: string;
  tipoEmpleo: string;
  fechaInicio: string;        // YYYY-MM-DD or ''
  rolesCertificados: string[];
  flags: UserFlags;
}

export interface MonthlyStats {
  mesLabel: string;           // 'Mayo 2026'
  eventCount: number;
  totalGanado: number;
}

export interface CareerStats {
  totalEvents: number;
  totalGanado: number;
  primerEvento: string;       // YYYY-MM-DD or ''
}

export interface Profile {
  info: ProfileInfo;
  stats: {
    thisMonth: MonthlyStats;
    career: CareerStats;
  };
}

export async function fetchProfile(): Promise<Profile> {
  const r = await request<Profile>('/me/profile');
  setCached(CACHE_KEYS.profile, r, TTL_1HOUR);
  return r;
}

// ============ Ofertas ============

export interface Offer {
  eventoId: string;
  rol: string;
  round: number;
  title: string;
  date: string;        // YYYY-MM-DD; '' if event row missing
  startTime: string;   // 'HH:MM AM/PM' from Hora_Llegada_Personal
  location: string;
  mapsUrl?: string;
  expiresAt: string;   // ISO timestamp; '' if missing
}

export async function fetchOffers(): Promise<{ offer: Offer | null }> {
  const r = await request<{ offer: Offer | null }>('/offers');
  setCached(CACHE_KEYS.offers, r, TTL_30SEC);
  return r;
}

export interface OfferDecisionResponse {
  ok: boolean;
  cached?: boolean;
}

export async function acceptOffer(): Promise<OfferDecisionResponse> {
  const r = await request<OfferDecisionResponse>('/offers/accept', { method: 'POST', body: '{}' });
  invalidateCached(CACHE_KEYS.offers, CACHE_KEYS.events);
  return r;
}

export async function rejectOffer(): Promise<OfferDecisionResponse> {
  const r = await request<OfferDecisionResponse>('/offers/reject', { method: 'POST', body: '{}' });
  invalidateCached(CACHE_KEYS.offers);
  return r;
}

// ============================================================================
// Event chat (Sophi App v2 Piece 1). Backend at /chat/:eventId on miniapp-api.
// ============================================================================

export interface ChatThread {
  eventId: string;
  /** Effective briefing: the ops/captain override when one exists, else the
   *  auto-generated official briefing (event data + live roster). */
  briefing: string;
  /** True when ops/captain stored an override; false = auto-generated. */
  briefingIsCustom?: boolean;
  capitanId: string | null;
  memberCount: number;
  lastMessageAt: string | null;
  archived: boolean;
  /** Server-computed: false pre-event for non-captain, non-admin staff.
   *  Treat undefined as true (older API). Client gating is UX only — the
   *  server enforces with 403 chat_not_open. */
  canPost?: boolean;
  /** 'YYYY-MM-DD' the composer unlocks for this caller; null when open. */
  opensOn?: string | null;
}

export interface ChatMessage {
  id: string;
  authorPersonalId: string | null;
  /** Resolved display name ("Yarisol M.") for the bubble label. Server falls
   *  back to the raw personalId when the staff isn't in Personal_Maestro;
   *  may be absent on optimistic local messages. */
  authorName?: string | null;
  body: string;
  kind: 'text' | 'system';
  createdAt: string | null;
  isCapitan: boolean;
}

export async function getChatThread(eventId: string): Promise<ChatThread> {
  return request<ChatThread>(`/chat/${encodeURIComponent(eventId)}`);
}

export async function getChatMessages(
  eventId: string,
  opts: { before?: string; limit?: number } = {}
): Promise<{ messages: ChatMessage[]; capitanId: string | null; canPost?: boolean }> {
  const qs: string[] = [];
  if (opts.before) qs.push(`before=${encodeURIComponent(opts.before)}`);
  if (opts.limit) qs.push(`limit=${opts.limit}`);
  const suffix = qs.length ? `?${qs.join('&')}` : '';
  return request(`/chat/${encodeURIComponent(eventId)}/messages${suffix}`);
}

export async function postChatMessage(
  eventId: string,
  body: string
): Promise<{ message: ChatMessage }> {
  return request(
    `/chat/${encodeURIComponent(eventId)}/messages`,
    { method: 'POST', body: JSON.stringify({ body }) }
  );
}

/** Admin/captain: edit the event's pinned briefing. Writes the sheet (source
 *  of truth) + the thread copy, posts a "📋 Briefing actualizado" system
 *  message and DMs the team. Empty text clears the override — the response
 *  carries the regenerated auto briefing. */
export async function updateChatBriefing(
  eventId: string,
  briefing: string
): Promise<{ ok: true; briefing: string; isCustom: boolean }> {
  return request(
    `/chat/${encodeURIComponent(eventId)}/briefing`,
    { method: 'PUT', body: JSON.stringify({ briefing }) }
  );
}

export async function markChatRead(eventId: string, messageId: string): Promise<void> {
  await request(
    `/chat/${encodeURIComponent(eventId)}/read`,
    { method: 'POST', body: JSON.stringify({ messageId }) }
  );
}

export async function getChatSubscription(
  eventId: string
): Promise<{ subscribed: boolean; lastReadMessageId: string | null }> {
  return request(`/chat/${encodeURIComponent(eventId)}/subscription`);
}

export async function setChatSubscription(
  eventId: string,
  subscribed: boolean
): Promise<{ ok: true; subscribed: boolean }> {
  return request(
    `/chat/${encodeURIComponent(eventId)}/subscription`,
    { method: 'POST', body: JSON.stringify({ subscribed }) }
  );
}

// ============================================================================
// Closure flow (Sophi App v2 Pieces 2-4). Backend at /events/:id/{...} on
// miniapp-api. All routes are event-scoped.
// ============================================================================

export interface ClosureTeamMember {
  staffId: string;
  nombre: string;
  rol: string;
  reported: boolean;
  source: 'app' | 'group' | null;
  ventas: number | null;
  propinas: number | null;
}

export interface MyReport {
  ventas: number;
  propinas: number;
  source: 'app' | 'group';
  edited: boolean;
}

export type ClosureState = 'not_initiated' | 'collecting' | 'finalizing';

export interface ClosureStatus {
  state: ClosureState;
  initiatedBy?: string | null;
  initiatedAt?: string | null;
  team: ClosureTeamMember[];
  remaining: { staffId: string; nombre: string }[];
  myReport: MyReport | null;
  isCaptain: boolean;
  isAdmin?: boolean;
  /** May drive closure at all (captain OR supervisor OR admin). Gates the
   *  "solo el capitán cierra" blocked view — NOT canInitiate/canFinalize, which
   *  both go false mid-collection for a non-captain closer. */
  isCloser?: boolean;
  canInitiate: boolean;
  canFinalize: boolean;
  /** Admin force-close: bypasses all-reports-in gate. Requires reason. */
  canForceFinalize?: boolean;
  /**
   * Deliberate-choice tip config, surfaced only in `not_initiated` state for a
   * trusted closer so the screen can offer inline selectors on a captain-less
   * event. `tipDistribution` null = not yet chosen; `otrosPendientes` = Otro
   * rows still missing their in-pool flag.
   */
  tipDistribution?: 'Solo_Camareros' | 'Todos_Los_Roles' | 'Pool_Total' | null;
  otrosPendientes?: { staffId: string; nombre: string }[];
  /**
   * Fase 2 closure gate (cutover plan): the event has staff requested but not
   * a single positive tarifa/pago. Unlike the tip config this is NOT
   * resolvable inline — Ventas completes the rates in the CRM — so the screen
   * renders it as a blocker on "Iniciar cierre", not a selector.
   */
  tarifasPendientes?: boolean;
}

// Ops tab (admin-only) — today's events with closure progress.
export type ArrivalStatus = 'llego_a_tiempo' | 'llego_tarde' | 'por_llegar' | 'no_ha_llegado' | 'na';

export interface RosterMember {
  staffId: string;
  nombre: string;
  rol: string;
  arrivalStatus: ArrivalStatus;
  /** Minutes late (llego_tarde) / minutes overdue (no_ha_llegado), else null. */
  deltaMin: number | null;
  /** Arrival time formatted in AST, e.g. "6:12 PM"; '' if no check-in. */
  checkInLocal: string;
  /** Telegram handle/number for safety follow-up; '' if unknown. */
  telegram: string;
  /** Acknowledged the event (Estado Confirmado). */
  acknowledged: boolean;
  /** Has submitted their post-event report (live closure state or sheet). */
  reported: boolean;
}

export interface ArrivalSummary {
  llegaron: number;
  tarde: number;
  porLlegar: number;
  noLlegaron: number;
  total: number;
}

export interface OpsEventSummary {
  id: string;
  title: string;
  date: string;
  startTime: string;
  location: string;
  estado: string;
  capitanId: string | null;
  capitanNombre: string | null;
  closureState: 'not_initiated' | 'collecting' | 'finalizing';
  reportedCount: number;
  teamSize: number;
  roster: RosterMember[];
  arrivalSummary: ArrivalSummary;
  /** 0 = today; N = event was N days ago and still not closed (overdue). */
  daysOverdue?: number;
}

/** Lightweight upcoming-event entry (next 14 days) for the Ops tab — enough
 *  to identify the event and open its chat; no closure/arrival tracking. */
export interface OpsUpcomingEvent {
  id: string;
  title: string;
  date: string;
  startTime: string;
  location: string;
  capitanNombre: string | null;
  teamSize: number;
}

export async function getOpsEventsToday(): Promise<{ events: OpsEventSummary[]; upcoming?: OpsUpcomingEvent[]; cerradosHoy?: number }> {
  return request(`/events/today-all`);
}

// ============================================================================
// Swap flow (Sophi App v2) — staff finds own replacement, no penalty.
// Since 2026-07-20: target accept moves the request to 'ops_pending'; the
// sheet writes happen only after Operaciones approves (decide returns
// status 'ops_pending' instead of 'accepted').
// ============================================================================

export interface SwapCandidate {
  staffId: string;
  nombre: string;
  roles: string[];
  tipoEmpleo: string;
  esCapitan: boolean;
  nivelSenioridad: string;
  /** Same seniority as the requester for camarero turnos — ranked first by the server. */
  recomendado?: boolean;
}

export interface SwapCandidatesResponse {
  sourceRol: string;
  eventoFecha: string;
  candidates: SwapCandidate[];
}

export interface SwapRequestCreated {
  reqId: string;
  expiresAt: string;
  ttlSec: number;
  toName: string;
  toChatId: string | null;
}

export interface SwapOffer {
  reqId: string;
  eventoId: string;
  eventoFecha: string;
  fromStaffId: string;
  fromName: string;
  rol: string;
  requestedAt: string;
  expiresAt: string;
}

export async function getSwapCandidates(eventId: string): Promise<SwapCandidatesResponse> {
  return request(`/events/${encodeURIComponent(eventId)}/swap-candidates`);
}

export async function requestSwap(
  eventId: string,
  targetStaffId: string
): Promise<SwapRequestCreated> {
  return request(
    `/events/${encodeURIComponent(eventId)}/swap-request`,
    { method: 'POST', body: JSON.stringify({ targetStaffId }) }
  );
}

export async function getMySwapOffers(): Promise<{ offers: SwapOffer[] }> {
  return request(`/swaps/mine`);
}

export async function decideSwapOffer(
  reqId: string,
  decision: 'accept' | 'reject'
): Promise<{ ok: true; status: string }> {
  return request(
    `/swaps/${encodeURIComponent(reqId)}/decide`,
    { method: 'POST', body: JSON.stringify({ decision }) }
  );
}

export async function withdrawSwap(reqId: string): Promise<{ ok: true; status: string }> {
  return request(
    `/swaps/${encodeURIComponent(reqId)}/withdraw`,
    { method: 'POST', body: JSON.stringify({}) }
  );
}

export async function getClosureStatus(eventId: string): Promise<ClosureStatus> {
  return request<ClosureStatus>(`/events/${encodeURIComponent(eventId)}/closure-status`);
}

export async function initiateClosure(eventId: string): Promise<{ status: string; initiatedAt?: string }> {
  return request(
    `/events/${encodeURIComponent(eventId)}/initiate-closure`,
    { method: 'POST', body: JSON.stringify({}) }
  );
}

/**
 * Set tip distribution inline before initiating closure — the captain-less
 * path where nobody picked it in the group. Trusted-closer gated server-side.
 */
export async function setTipDistribution(
  eventId: string,
  distribucion: 'Solo_Camareros' | 'Todos_Los_Roles' | 'Pool_Total'
): Promise<{ ok: true; distribucion: string }> {
  return request(
    `/events/${encodeURIComponent(eventId)}/tip-distribution`,
    { method: 'POST', body: JSON.stringify({ distribucion }) }
  );
}

/** Set whether "Otro"-role staff join the tip pool — resolves otros_sin_flag. */
export async function setOtrosPropinas(
  eventId: string,
  valor: 'Si' | 'No'
): Promise<{ ok: true; valor: string; updated: number }> {
  return request(
    `/events/${encodeURIComponent(eventId)}/otros-propinas`,
    { method: 'POST', body: JSON.stringify({ valor }) }
  );
}

export interface MyReportSubmission {
  ventas: number;
  propinas: number;
  force?: boolean;
}

export interface MyReportConflict {
  error: 'already_reported' | 'already_edited' | 'no_state' | 'staff_not_in_team';
  existing: { ventas: number; propinas: number; source: 'app' | 'group'; edited: boolean } | null;
}

export interface MyReportSuccess {
  kind: 'recorded' | 'edited' | 'finalized';
  remaining: { staffId: string; nombre: string }[] | null;
  oldVentas: number | null;
  oldPropinas: number | null;
}

export async function submitMyReport(eventId: string, p: MyReportSubmission): Promise<MyReportSuccess> {
  return request<MyReportSuccess>(
    `/events/${encodeURIComponent(eventId)}/my-report`,
    { method: 'POST', body: JSON.stringify(p) }
  );
}

/**
 * Captain/supervisor/admin submits a report on behalf of another team member.
 * Mirrors the group flow's `venta X propina Y de [Nombre]` pattern with
 * explicit staffId selection — no fuzzy name match needed.
 */
export async function submitReportFor(
  eventId: string,
  p: { targetStaffId: string; ventas: number; propinas: number; force?: boolean }
): Promise<MyReportSuccess> {
  return request<MyReportSuccess>(
    `/events/${encodeURIComponent(eventId)}/report-for`,
    { method: 'POST', body: JSON.stringify(p) }
  );
}

export async function finalizeClosure(
  eventId: string,
  p: { force?: boolean; reason?: string } = {}
): Promise<{ status: string }> {
  return request(
    `/events/${encodeURIComponent(eventId)}/finalize-closure`,
    { method: 'POST', body: JSON.stringify(p) }
  );
}

// ─── /events/:id/results — post-closure team results + ranking (no pay) ──────

export interface ResultRow {
  staffId: string;
  nombre: string;
  rol: string;
  ventas: number;
  /** Gross tips reported — the ranking key. */
  propBrutas: number;
  /** Net tips after pool distribution. */
  propNeta: number;
  pctPropinas: number;
  cumpleMeta: boolean;
  /** True for the viewer's own row (highlight it). */
  isMe: boolean;
  /** Only on "Otro" rows: kept their own tips (out of pool). */
  fueraDelPool?: boolean;
}

export interface EventResults {
  /** false until the event is Completado/Cerrado — UI shows "calculando…". */
  ready: boolean;
  estado?: string;
  evento: {
    id: string;
    title: string;
    cliente: string;
    distribucion: 'Solo_Camareros' | 'Todos_Los_Roles' | 'Pool_Total' | null;
  };
  tipTarget?: number;
  totals?: { ventas: number; propinas: number; pctEquipo: number; goalMet: boolean; personalReal: number };
  top?: {
    ventas: { nombre: string; valor: number } | null;
    propinas: { nombre: string; valor: number } | null;
  };
  camareros?: ResultRow[];
  supervisores?: ResultRow[];
  bartenders?: ResultRow[];
  otros?: ResultRow[];
}

export async function getEventResults(eventId: string): Promise<EventResults> {
  return request<EventResults>(`/events/${encodeURIComponent(eventId)}/results`);
}

// ─── /events/:id peer ratings (Calificaciones, Tipo=Colega) ────────────────

export interface RateableTeammate {
  staffId: string;
  nombre: string;
  rol: string;
  alreadyRated: boolean;
  myStars: number | null;
}

export interface RateableTeammatesResponse {
  ready: boolean;
  estado?: string;
  /** True once the caller already went through the rating screen for this
   * event (submit-once) — the screen must not reopen. */
  alreadyReviewed?: boolean;
  teammates: RateableTeammate[];
}

export interface PeerRatingInput {
  targetStaffId: string;
  estrellas: number;
  comentario?: string;
}

export interface PeerRatingResult {
  targetStaffId: string;
  status: string;
  reason?: string;
  id?: string;
}

export async function getRateableTeammates(eventId: string): Promise<RateableTeammatesResponse> {
  return request<RateableTeammatesResponse>(`/events/${encodeURIComponent(eventId)}/rateable-teammates`);
}

export async function submitPeerRatings(
  eventId: string,
  ratings: PeerRatingInput[],
): Promise<{ ok: boolean; results: PeerRatingResult[] }> {
  return request(`/events/${encodeURIComponent(eventId)}/peer-ratings`, {
    method: 'POST',
    body: JSON.stringify({ ratings }),
  });
}

export interface PendingRatingEvent {
  eventId: string;
  title: string;
  fecha: string | null;
  teammateCount: number;
}

// Closed events the caller still needs to peer-rate (drives the Inicio nudge).
export async function getPendingPeerRatings(): Promise<{ pending: PendingRatingEvent[] }> {
  return request<{ pending: PendingRatingEvent[] }>('/events/pending-ratings');
}

export interface MyRating {
  /** Overall (blended peer+client) score, or null when not enough ratings yet. */
  score: number | null;
  visible: boolean;
}

// The caller's own overall rating (Phase-2, Uber-style) — reads the weekly snapshot.
export async function getMyRating(): Promise<MyRating> {
  return request<MyRating>('/me/rating');
}

// ─── /rankings — cross-staff leaderboards ──────────────────────────────────

export interface LeaderboardEntry {
  rank: number;
  personalId: string;
  displayName: string;
  eventCount: number;
  /** Present on the Top Performer (ratio) tab — propinas / ventas, e.g. 0.145 = 14.5%. */
  tipsRatio?: number | null;
  /** Present on Ventas + Top Performer tabs. */
  totalVentas?: number;
  /** Present on Propinas + Top Performer tabs. */
  totalPropinas?: number;
  /** Only present on ventas/propinas lists. Null when median is zero. */
  pctVsMedian?: number | null;
  /** True when this row is the authenticated viewer — UI highlights it. */
  isViewer: boolean;
}

export interface ViewerPosition {
  /** Null when the viewer has no Completado events this period (UI shows
   *  "Sin eventos") OR — on the Top Performer tab — when they don't meet the
   *  qualification threshold. */
  rank: number | null;
  /** Total entrants in this specific leaderboard. */
  total: number;
  eventCount: number;
  totalVentas?: number;
  totalPropinas?: number;
  pctVsMedian?: number | null;
}

/** Extended position for the Top Performer (ratio) tab. Carries both
 *  qualification thresholds and identifies which gate the viewer is failing
 *  so the UI can render the most actionable message. */
export interface PerformerViewerPosition extends ViewerPosition {
  tipsRatio?: number | null;
  qualifies: boolean;
  eventThreshold: number;
  ventasThreshold: number;
  /** null when qualifies. 'no_events' = zero activity. 'events' = below event
   *  threshold. 'ventas' = passed events but below ventas threshold. */
  failedGate: 'no_events' | 'events' | 'ventas' | null;
}

export interface TopPerformersResponse {
  period: {
    startISO: string;
    endISO: string;
    label: string;
  };
  qualifyThreshold: number;
  ventasThreshold: number;
  topPerformer: LeaderboardEntry[];
  mostActive: LeaderboardEntry[];
  topVentas: LeaderboardEntry[];
  topPropinas: LeaderboardEntry[];
  viewer: {
    performer: PerformerViewerPosition | null;
    mostActive: ViewerPosition | null;
    ventas: ViewerPosition | null;
    propinas: ViewerPosition | null;
  };
}

export async function getTopPerformers(): Promise<TopPerformersResponse> {
  return request<TopPerformersResponse>('/rankings/top');
}

// ============================================================================
// Tienda de uniformes (MA-11 Phase 2). Backend at /store on miniapp-api. Orders
// deduct from the staffer's earned money via the Cargo mechanism — the charge
// surfaces in the Pagos screen as a "Cargo por uniforme" deduction.
// ============================================================================

export interface StoreProduct {
  sku: string;
  nombre: string;
  descripcion: string;
  tallas: string[];
  precio: number;
  stock: number;
  lugarRecogida: string;
  fotoUrl: string;
}

export interface StoreCredit {
  /** max(0, Saldo_Tarifa_Acumulado − Saldo_Penalidad). */
  neto: number;
  /** Flat RD$ cushion above neto (STORE_CREDIT_LIMIT_DOP). */
  creditLimit: number;
  /** neto + creditLimit — the most a staffer may spend right now. */
  available: number;
}

export interface StoreOrderItem {
  sku: string;
  nombre: string;
  talla: string;
  cantidad: number;
  precioUnit: number;
  precioTotal: number;
}

export interface StoreOrder {
  idCompra: string;
  fecha: string;
  estado: string;            // 'Pendiente' | 'Despachado' | 'Cancelado'
  lugarRecogida: string;
  idCargo?: string;
  total: number;
  items: StoreOrderItem[];
}

/** Cart line as sent to POST /store/orders. */
export interface CartLine { sku: string; talla: string; cantidad: number; }

export async function getStoreProducts(): Promise<{ products: StoreProduct[] }> {
  const r = await request<{ products: StoreProduct[] }>('/store/products');
  setCached(CACHE_KEYS.store, r, TTL_5MIN);
  return r;
}

export async function getStoreCredit(): Promise<StoreCredit> {
  return request<StoreCredit>('/store/credit');
}

export interface CreateStoreOrderResponse {
  ok: boolean;
  cached?: boolean;
  order: StoreOrder;
  available?: number;
}

export async function createStoreOrder(items: CartLine[]): Promise<CreateStoreOrderResponse> {
  const r = await request<CreateStoreOrderResponse>('/store/orders', {
    method: 'POST',
    body: JSON.stringify({ items }),
  });
  // Charge applied → balance changed; stock changed → product list stale.
  invalidateCached(CACHE_KEYS.balance, CACHE_KEYS.store);
  return r;
}

export async function getMyStoreOrders(): Promise<{ orders: StoreOrder[] }> {
  return request<{ orders: StoreOrder[] }>('/store/orders/mine');
}

export async function cancelStoreOrder(
  idCompra: string
): Promise<{ ok: boolean; idCompra: string; reversed: number }> {
  const r = await request<{ ok: boolean; idCompra: string; reversed: number }>(
    `/store/orders/${encodeURIComponent(idCompra)}/cancel`,
    { method: 'POST', body: '{}' }
  );
  invalidateCached(CACHE_KEYS.balance, CACHE_KEYS.store);
  return r;
}

export { ApiError };
